import styled from '@emotion/styled'
import { playButtonClass } from '../../../../buttons/PlayButton/PlayButton.styled'
import { VideoMetadata } from '../../../../content/Video/components/common/UnstyledVideoMetadata'
import { colors } from '../../../../__styling/settings/colors'
import { zIndex } from '../../../../__styling/settings/z-index'
import { breakpoint } from '../../../../__styling/style-functions'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
import { hexToRGBA } from '../../../../__styling/style-mixins'
import { themedValue } from '../../../../__styling/themed-value'
import { Theme } from '../../../../__styling/themes'

export interface StyledTitleOverlayProps {
    compactMode: boolean
}

const themedColor = (theme: Theme) =>
    themedValue(theme, {
        thewest: colors.black,
        perthnow: colors.white,
        sevennews: colors.white,
        fallback: undefined,
    })

const primaryColor = (theme: Theme) =>
    themedValue(theme, {
        thewest: (section) => section.primaryColor,
        perthnow: colors.perthnow.pinkThulite,
        sevennews: (resolvedSection) => resolvedSection.video.primaryColor,
        fallback: undefined,
    })

export const StyledTitleOverlay = styled('div')<StyledTitleOverlayProps>(
    {
        alignItems: 'center',
        zIndex: zIndex.common.videoTitleOverlay,
        right: 0,
        bottom: 0,
        left: 0,
        position: 'absolute',
        paddingRight: calcRem(15),
    },
    ({ theme }) => ({
        display: themedValue(theme, {
            sevennews: 'none',
            fallback: 'flex',
        }),
        paddingLeft: themedValue(theme, {
            sevennews: calcRem(18),
            fallback: calcRem(75),
        }),
        height: themedValue(theme, {
            sevennews: calcRem(62),
            fallback: calcRem(80),
        }),
        [breakpoint('sm')]: {
            height: themedValue(theme, {
                sevennews: calcRem(62),
                fallback: calcRem(100),
            }),
            paddingLeft: themedValue(theme, {
                sevennews: calcRem(18),
                fallback: calcRem(120),
            }),
        },
        fontFamily: themedValue(theme, {
            sevennews: theme.fonts.sansSerifCond,
            fallback: theme.fonts.sansSerif,
        }),
        background: themedValue(theme, {
            thewest: `hsla(0,  0%, 100%, .9)`,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: hexToRGBA(colors.black, 0.7),
            fallback: undefined,
        }),
        color: themedColor(theme),

        [`& .${playButtonClass}`]: {
            bottom: themedValue(theme, {
                sevennews: calcRem(18),
                fallback: undefined,
            }),
            [breakpoint('sm')]: {
                bottom: themedValue(theme, {
                    sevennews: calcRem(30),
                    fallback: undefined,
                }),
            },
        },
    }),
    ({ compactMode, theme }) =>
        compactMode
            ? {
                  height: themedValue(theme, {
                      sevennews: calcRem(40),
                      fallback: undefined,
                  }),
                  [breakpoint('sm')]: {
                      height: themedValue(theme, {
                          sevennews: calcRem(62),
                          fallback: calcRem(100),
                      }),
                  },
                  paddingLeft: themedValue(theme, {
                      sevennews: calcRem(18),
                      fallback: calcRem(112),
                  }),
              }
            : {},
)

export const StyledInset = styled('div')(
    {
        lineHeight: 1,
        width: '100%',
        maxWidth: '100%',

        [breakpoint('sm')]: {
            transform: 'none',
            paddingTop: 0,
            paddingBottom: 0,
        },
    },
    ({ theme }) => ({
        // align to the play button (it's misaligned)
        // TODO: fix misaligned play button
        transform: themedValue(theme, {
            sevennews: undefined,
            fallback: `translateY(${calcRem(3)})`,
        }),
        padding: themedValue(theme, {
            sevennews: `${calcRem(4)} 0`,
            fallback: `${calcRem(4)} 0 ${calcRem(4)} ${calcRem(10)}`,
        }),
        borderLeft: themedValue(theme, {
            sevennews: undefined,
            fallback: `${calcRem(4)} solid ${primaryColor(theme)}`,
        }),
    }),
)

export const StyledTitles = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
})

export interface StyledHeadingProps {
    autoPlayIsEnabled: boolean
    showVideoTitle?: boolean
}

export const StyledHeading = styled('h3')<StyledHeadingProps>(
    ({ theme, showVideoTitle }) => ({
        fontSize: themedValue(theme, {
            sevennews: calcRem(14),
            fallback: calcRem(18),
        }),
        fontWeight: showVideoTitle ? 'normal' : 'revert',
        overflow: showVideoTitle ? 'visible' : 'hidden',
        margin: 0,
        padding: 0,
        flex: themedValue(theme, { sevennews: undefined, fallback: '1 1 70%' }),
        textOverflow: 'ellipsis',
        whiteSpace: showVideoTitle ? 'normal' : 'nowrap',
        lineHeight: 1.2, // need to set this because of the overflow hidden
        verticalAlign: 'middle',

        [breakpoint('md')]: {
            fontSize: themedValue(theme, {
                sevennews: calcRem(17),
                fallback: calcRem(22),
            }),
        },

        '&::after': themedValue(theme, {
            sevennews: {
                content: '"  "',
                whiteSpace: 'pre',
            },
            fallback: undefined,
        }),
    }),
    (props) =>
        props.autoPlayIsEnabled && {
            // if autoplay is enabled, we don't show the titles on countdown on mobile
            display: 'none',
            [breakpoint('sm')]: {
                display: 'block',
            },
        },
)

export const StyledVideoDuration = styled('span')(({ theme }) => ({
    display: themedValue(theme, {
        sevennews: 'inline-block',
        fallback: 'none',
    }),

    [breakpoint('sm')]: {
        display: 'none',
    },
    paddingLeft: calcRem(5),
}))

export const StyledVideoMetadata = styled(VideoMetadata)(({ theme }) => [
    {
        display: 'none',
        margin: themedValue(theme, {
            sevennews: 0,
            fallback: `0 0 0 ${calcRem(15)}`,
        }),
        padding: 0,
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        lineHeight: themedValue(theme, { sevennews: 1.2, fallback: undefined }),

        [breakpoint('sm')]: {
            display: themedValue(theme, {
                sevennews: 'inline-block',
                fallback: 'block',
            }),
            fontSize: calcRem(14),
        },

        [breakpoint('md')]: {
            fontSize: calcRem(16),
        },
    },
    theme.kind === 'sevennews' && {
        time: {
            fontWeight: 600,
        },
    },
])

export const StyledAutoplayWrap = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    [breakpoint('sm')]: {
        marginTop: themedValue(theme, {
            sevennews: calcRem(4),
            fallback: calcRem(6),
        }),
    },
    fontFamily: themedValue(theme, {
        sevennews: theme.fonts.sansSerif,
        fallback: undefined,
    }),
}))

export const StyledCountdown = styled('p')(({ theme }) => ({
    fontSize: themedValue(theme, {
        sevennews: calcRem(14),
        fallback: calcRem(16),
    }),
    margin: themedValue(theme, {
        sevennews: `0 ${calcRem(8)} 0 0`,
        fallback: `0 ${calcRem(15)} 0 0`,
    }),
    lineHeight: 1,
}))

export const StyledCountdownTime = styled('span')({
    fontWeight: 700,
})

export const StyledCancel = styled('button')(({ theme }) => ({
    margin: 0,
    padding: 0,
    lineHeight: 1,
    cursor: 'pointer',

    // on the old video player these came from brightcove
    background: 'none',
    border: 'none',
    color: themedColor(theme),
    fontSize: themedValue(theme, {
        sevennews: calcRem(13),
        fallback: calcRem(16),
    }),
    fontWeight: themedValue(theme, {
        sevennews: 400,
        fallback: 700,
    }),
    textTransform: themedValue(theme, {
        sevennews: undefined,
        fallback: 'uppercase' as const,
    }),
    textDecoration: themedValue(theme, {
        sevennews: 'underline',
        fallback: undefined,
    }),
    '&::before': {
        display: themedValue(theme, {
            sevennews: 'none',
            fallback: 'inline-block',
        }),
        marginRight: calcRem(5),
        width: calcRem(16),
        height: calcRem(16),
        content: `''`,
        verticalAlign: 'top',
        background: themedValue(theme, {
            thewest: colors.black,
            perthnow: colors.white,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),
    },
    '&:hover': {
        color: primaryColor(theme),
    },

    [breakpoint('sm')]: {
        display: 'inline-block',
    },
}))
